import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTechIdentity } from "../lib/TechIdentityContext";
import { useRoles } from "../lib/useRoles";
import { getTechs, getReadersForPicker } from "../lib/api";
import { techDisplayName } from "../lib/names";

/**
 * Admin-only "view as user" control, rendered in the sidebar above the user
 * tile. Switches the admin into a technologist's or an interpreting
 * physician's view of the portal.
 *
 * The two are not the same underneath. A tech's cases are filtered in the
 * browser, so viewing as one shows the admin a subset of a payload they were
 * served anyway: display-level, and not a security boundary. An IP's cases
 * are filtered in the query, so viewing as one sends the reader id with the
 * request and the server scopes the data. Without that the page would show
 * the physician's chrome over the admin's own data, which looks right and is
 * wrong.
 */
export default function ViewAsUser({ collapsed = false }) {
  const { realIsAdmin } = useRoles();
  const { viewingAsTech, viewingAsIP, impersonated, startImpersonation, stopImpersonation } =
    useTechIdentity();

  const [open, setOpen]   = useState(false);
  const [kind, setKind]   = useState("tech");   // "tech" | "ip"
  const [techs, setTechs] = useState(null);     // null = not loaded yet
  const [readers, setReaders] = useState(null);
  const [q, setQ]         = useState("");
  const boxRef = useRef(null);

  // Load the roster the first time the picker is opened, not on every mount -
  // most sessions never open it.
  // Each roster loads the first time its tab is opened, not on mount: most
  // sessions never open the picker, and many that do only need one of them.
  useEffect(() => {
    if (!open || kind !== "tech" || techs !== null) return;
    getTechs().then(list => setTechs(Array.isArray(list) ? list : []));
  }, [open, kind, techs]);

  useEffect(() => {
    if (!open || kind !== "ip" || readers !== null) return;
    getReadersForPicker()
      .then(r => setReaders(Array.isArray(r?.readers) ? r.readers : []))
      .catch(() => setReaders([]));
  }, [open, kind, readers]);

  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const roster = kind === "tech" ? techs : readers;

  const filtered = useMemo(() => {
    const list = roster || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(t =>
      `${t.name || ""} ${t.email || ""} ${t.techWebId || ""} ${t.readerId || ""}`
        .toLowerCase().includes(needle)
    );
  }, [roster, q]);

  // Never gate this on isAdmin: while impersonating, the effective role is
  // Tech, and gating on it would strip the only way back out.
  if (!realIsAdmin) return null;

  if ((viewingAsTech || viewingAsIP) && collapsed) {
    return (
      <button
        onClick={stopImpersonation}
        title={`Viewing as ${techDisplayName(impersonated?.name) || impersonated?.techWebId || impersonated?.readerId} - click to exit`}
        aria-label="Exit view"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: "100%", padding: "7px 0", marginBottom: 6, cursor: "pointer",
          borderRadius: 8, background: "rgba(245,158,11,0.16)",
          border: "1px solid rgba(245,158,11,0.5)", color: "#fbbf24",
        }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
             strokeWidth="1.5" strokeLinecap="round">
          <circle cx="8" cy="5.5" r="2.5" /><path d="M3 13.5c0-2.5 2-4 5-4s5 1.5 5 4" />
        </svg>
      </button>
    );
  }

  // The picker needs a search box and a roster list, neither of which fits a
  // 58px rail, so collapsed offers no way in. Getting out is handled above.
  if (collapsed) return null;

  if (viewingAsTech || viewingAsIP) {
    return (
      <div style={{
        margin: "0 12px 8px", padding: "8px 10px", borderRadius: 8,
        background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.45)",
      }}>
        <div style={{ fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase",
                      color: "#fbbf24", fontWeight: 700 }}>
          Viewing as {viewingAsIP ? "physician" : "tech"}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-on-dark)", margin: "2px 0 6px",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
             title={impersonated?.email || impersonated?.techWebId || ""}>
          {techDisplayName(impersonated?.name) || impersonated?.techWebId || impersonated?.readerId}
        </div>
        <button
          onClick={stopImpersonation}
          style={{
            width: "100%", padding: "5px 8px", fontSize: 12, fontWeight: 600,
            cursor: "pointer", borderRadius: 6, color: "#111827",
            background: "#fbbf24", border: "none",
          }}>
          Exit view
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} style={{ position: "relative", margin: "0 12px 8px" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", padding: "6px 10px", fontSize: 12, cursor: "pointer",
          borderRadius: 6, textAlign: "left", color: "var(--text-on-dark-dim)",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}>
        View as user...
      </button>

      {open && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: 0, right: 0,
          background: "#0f172a", border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          padding: 8, zIndex: 60,
        }}>
          <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
            {[["tech", "Technologist"], ["ip", "Physician"]].map(([k, label]) => (
              <button key={k} onClick={() => { setKind(k); setQ(""); }}
                style={{
                  flex: 1, padding: "4px 6px", fontSize: 11, cursor: "pointer", borderRadius: 5,
                  background: kind === k ? "rgba(37,99,235,0.3)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${kind === k ? "rgba(147,180,255,0.5)" : "rgba(255,255,255,0.12)"}`,
                  color: kind === k ? "#dbe7ff" : "var(--text-on-dark-dim)",
                  fontWeight: kind === k ? 600 : 400,
                }}>{label}</button>
            ))}
          </div>
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={kind === "tech" ? "Search technologists" : "Search physicians"}
            style={{
              width: "100%", boxSizing: "border-box", padding: "6px 8px",
              fontSize: 12, borderRadius: 6, marginBottom: 6,
              color: "var(--text-on-dark)", background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          />
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {roster === null ? (
              <div style={{ padding: 8, fontSize: 12, color: "var(--text-on-dark-dim)" }}>
                Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 8, fontSize: 12, color: "var(--text-on-dark-dim)" }}>
                {roster.length === 0
                  ? (kind === "tech" ? "No technologists found." : "No physicians found.")
                  : "No match."}
              </div>
            ) : filtered.map(t => (
              <button
                key={t.techWebId || t.readerId}
                onClick={() => { startImpersonation(t, kind); setOpen(false); setQ(""); }}
                style={{
                  display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                  padding: "6px 8px", fontSize: 12, borderRadius: 6,
                  background: "transparent", border: "none",
                  color: "var(--text-on-dark)",
                }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {techDisplayName(t.name) || t.techWebId || t.readerId}
                </div>
                {t.email && t.email !== t.name && (
                  <div style={{ fontSize: 10, color: "var(--text-on-dark-dim)",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t.email}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
